import { DownOutlined, LoadingOutlined } from '@ant-design/icons';
import { Empty } from 'antd';
import {
  OrderListSearchParams,
  TradeWithQueryParams,
  UseOrderListResult,
} from 'hooks/business/chat/useOrderList';
import { OrderCard } from './OrderCard';
import { OrderSkeleton } from './OrderCard/OrderSkeleton';
import { useEffect, useMemo, useRef } from 'react';
import { useInViewport, useLocalStorageState } from 'ahooks';
import dayjs from 'lib/dayjs';
import { useTranslate } from '@tolgee/react';
import { SearchInput } from './SearchInput';
import { useOrderHistoryVisible } from 'hooks/business/chat/useOrderHistoryVisible';

interface OrderListProps extends UseOrderListResult {
  conversation: Conversation.Conversation;
  isShopeeNotSupportOverThreeMonth: boolean;
  searchParams?: OrderListSearchParams;
  onSearchParamsChange: (params?: OrderListSearchParams) => void;
}

interface OrderListHistoryProps {
  visible: boolean;
  onToggle: () => void;
}

const OrderListHistory = ({ visible, onToggle }: OrderListHistoryProps) => {
  const { t } = useTranslate();
  return (
    <div
      className="flex items-center justify-center h-auto text-[#515359] cursor-pointer hover:opacity-80"
      onClick={onToggle}
    >
      {t('查看历史订单')}
      <DownOutlined
        className="ml-1.5 text-[10px]"
        style={{
          transform: visible ? 'rotate(180deg)' : 'rotate(0deg)',
          transition: 'transform 0.2s ease',
        }}
      />
    </div>
  );
};

interface OrderListFooterProps {
  hasNext: boolean;
  onNextPage: () => void;
  showEndText?: boolean;
  endText?: string;
}

const OrderListFooter = ({
  hasNext,
  onNextPage,
  showEndText = false,
  endText = '',
}: OrderListFooterProps) => {
  const ref = useRef<HTMLDivElement>(null);
  const [inViewport] = useInViewport(ref);

  const showLoading = inViewport && hasNext;

  useEffect(() => {
    if (showLoading) {
      onNextPage();
    }
  }, [showLoading, onNextPage]);

  return (
    <div ref={ref} className="flex items-center justify-center">
      {showLoading ? (
        <span className="h-[28px] text-[#515359] flex items-center justify-center cursor-pointer hover:opacity-80">
          <LoadingOutlined spin />
        </span>
      ) : null}
      {!hasNext && showEndText ? (
        <span className="text-xs text-[#84868C] inline-block max-w-[280px] mt-2 mb-1 text-center">
          {endText}
        </span>
      ) : null}
    </div>
  );
};

interface OrderListCoreProps {
  isShopeeNotSupportOverThreeMonth: boolean;
  isLoading: boolean;
  isEmpty: boolean;
  conversation: Conversation.Conversation;
  recentOrders: TradeWithQueryParams[];
  historyOrders: TradeWithQueryParams[];
  historyVisible: boolean;
  onToggleHistory: () => void;
  hasNext: boolean;
  fetchNextPage: () => void;
}

const OrderListCore = (props: OrderListCoreProps) => {
  const {
    isShopeeNotSupportOverThreeMonth,
    isLoading,
    isEmpty,
    conversation,
    recentOrders,
    historyOrders,
    hasNext,
    fetchNextPage,
  } = props;
  const { historyVisible, onToggleHistory } = props;
  const { t } = useTranslate();
  const showHistoryButton = historyOrders.length > 0;

  if (isLoading) {
    return <OrderSkeleton />;
  }

  if (isEmpty) {
    return (
      <div className="flex flex-col py-4">
        <Empty
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          description={
            isShopeeNotSupportOverThreeMonth ? (
              <div className="px-6">
                {t('根据Shopee平台政策，超过3个月的订单信息不支持展示，请前往卖家中心查看历史订单')}
              </div>
            ) : undefined
          }
        />
      </div>
    );
  }

  const endText = isShopeeNotSupportOverThreeMonth
    ? t('根据Shopee平台政策，超过3个月的订单信息不支持展示，请前往卖家中心查看历史订单')
    : t('sidebar.order.list.noMore');

  const hasRecentOrders = recentOrders.length > 0;

  return (
    <div className="flex-1 flex flex-col overflow-y-auto py-3 gap-3">
      {hasRecentOrders ? (
        recentOrders.map((order) => (
          <OrderCard key={order.internalTradeId} conversation={conversation} order={order} />
        ))
      ) : historyVisible ? null : (
        <Empty
          className="!mt-[90px] mb-[24px]"
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          description={t('sidebar.order.list.empty.noRecentOrders')}
        />
      )}
      {showHistoryButton && (
        <OrderListHistory visible={historyVisible} onToggle={onToggleHistory} />
      )}
      {historyVisible &&
        historyOrders.map((order) => (
          <OrderCard key={order.internalTradeId} conversation={conversation} order={order} />
        ))}
      <OrderListFooter
        hasNext={showHistoryButton ? historyVisible && hasNext : hasNext}
        onNextPage={fetchNextPage}
        showEndText={showHistoryButton ? !hasNext && historyVisible : !hasNext}
        endText={endText}
      />
    </div>
  );
};

/** 搜索结果列表（仅 searchMode 时展示）：扁平列表 + 搜索空态/底栏 */
interface OrderListSearchResultProps {
  isShopeeNotSupportOverThreeMonth: boolean;
  isLoading: boolean;
  orderList: TradeWithQueryParams[];
  conversation: Conversation.Conversation;
  hasNext: boolean;
  fetchNextPage: () => void;
}

const OrderListSearchResult = (props: OrderListSearchResultProps) => {
  const { t } = useTranslate();
  const {
    isShopeeNotSupportOverThreeMonth,
    isLoading,
    orderList,
    conversation,
    hasNext,
    fetchNextPage,
  } = props;

  if (isLoading) {
    return <OrderSkeleton />;
  }

  if (!orderList.length) {
    return (
      <div className="flex-1 flex flex-col overflow-y-auto py-3">
        <Empty
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          description={
            <>
              <div>{t('searchBar.noMatch')}</div>
              <div>{t('searchBar.match30')}</div>
            </>
          }
        />
      </div>
    );
  }

  return (
    <div className="flex-1 flex flex-col overflow-y-auto py-3 gap-3">
      {orderList.map((order) => (
        <OrderCard key={order.internalTradeId} conversation={conversation} order={order} />
      ))}
      <OrderListFooter
        hasNext={hasNext}
        onNextPage={fetchNextPage}
        showEndText={!hasNext}
        endText={
          isShopeeNotSupportOverThreeMonth
            ? t('根据Shopee平台政策，超过3个月的订单信息不支持展示，请前往卖家中心查看历史订单')
            : t('searchBar.match30')
        }
      />
    </div>
  );
};

export const OrderList = (props: OrderListProps) => {
  const {
    conversation,
    orderList,
    isLoading,
    hasNext = false,
    fetchNextPage,
    searchParams,
    onSearchParamsChange,
    isShopeeNotSupportOverThreeMonth,
  } = props;
  const { t } = useTranslate();

  const [searchType, setSearchType] = useLocalStorageState<string>(
    'trade_sider_search_type_value',
    {
      defaultValue: 'external_trade_ids',
    },
  );

  const cutoff = useMemo(() => dayjs().subtract(3, 'month'), []);

  const conversationId = conversation?.conversationId;
  const { historyVisible, toggleHistoryVisible: onToggleHistory } =
    useOrderHistoryVisible(conversationId);

  const { recentOrders, historyOrders } = useMemo(() => {
    const recent: typeof orderList = [];
    const history: typeof orderList = [];
    orderList.forEach((order) => {
      if (dayjs(Number(order.createdTimeInMs)).isBefore(cutoff)) {
        history.push(order);
      } else {
        recent.push(order);
      }
    });
    return { recentOrders: recent, historyOrders: history };
  }, [orderList, cutoff]);

  const searchMode =
    Object.values(searchParams ?? {}).filter((v) => v != null && v !== '').length > 0;

  const onSearch = (value: string) => {
    if (value?.trim()) {
      onSearchParamsChange({ [searchType as keyof OrderListSearchParams]: value.trim() });
    } else {
      onSearchParamsChange(undefined);
    }
  };

  const selectOptions = [
    { value: 'external_trade_ids', label: t('searchBar.external_trade_ids') },
    { value: 'trackingNumber', label: t('物流单号') },
    { value: 'itemName', label: t('searchBar.itemName') },
    { value: 'spuId', label: t('searchBar.spuId') },
    { value: 'sku', label: t('searchBar.sku') },
    { value: 'sellerSku', label: t('searchBar.sellerSku') },
  ];

  return (
    <div className="flex-1 flex flex-col overflow-hidden">
      <div className="h-[56px] flex items-center justify-center px-4 py-3 bg-[#FFFFFF]">
        <SearchInput
          selectOptions={selectOptions}
          selectValue={searchType}
          onSelectChange={setSearchType}
          onSearch={onSearch}
          placeholder={t(`placeholder.${searchType}`)}
        />
      </div>
      {searchMode ? (
        <OrderListSearchResult
          isShopeeNotSupportOverThreeMonth={isShopeeNotSupportOverThreeMonth}
          isLoading={isLoading}
          orderList={orderList}
          conversation={conversation}
          hasNext={hasNext}
          fetchNextPage={fetchNextPage}
        />
      ) : (
        <OrderListCore
          isShopeeNotSupportOverThreeMonth={isShopeeNotSupportOverThreeMonth}
          isLoading={isLoading}
          isEmpty={!orderList.length}
          conversation={conversation}
          recentOrders={recentOrders}
          historyOrders={historyOrders}
          historyVisible={historyVisible}
          onToggleHistory={onToggleHistory}
          hasNext={hasNext}
          fetchNextPage={fetchNextPage}
        />
      )}
    </div>
  );
};
